import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Chip, ChipProps, heightMap } from '../Chip';
import { useFullTheme } from '~/design';
import { InternalText } from '~/internal';
import { findChildrenOfType, isChildType } from '~/utils';

//https://writingjavascript.com/how-do-you-debounce-in-javascript#:~:text=Debounce%20timeout%20for%20fluid%20updates
const DEBOUNCE_TIMEOUT = 66;

export interface ChipGroupProps {
  /** The chips */
  children:
    | React.ReactElement<ChipProps>
    | Array<React.ReactElement<ChipProps>>
    | React.ReactFragment;

  /** Emphasis for chips on group */
  emphasis?: ChipProps['emphasis'];

  /** How many lines to show the chips when collapsed. Can be a number or `all`. Defaults to 1. */
  lines?: number | 'all';

  /** Size of chips on group */
  size?: ChipProps['size'];

  /**
   * Variant for chips on group
   * @deprecated Use `emphasis` instead.
   */
  variant?: ChipProps['variant'];

  /** The (max) width of the chips in the group.
   * `md` will set a max width of 220px, `hug` will make the chip fit its content. */
  width?: ChipProps['width'];
}

/**
 *
 * ChipGroup, as the name states, allows Chips to be grouped. It provides functionality to collapse the group
 * in lines, accepting as a prop how many lines will the group be collapsed into. When collapsed, ChipGroup
 * provides a couple of "control" chips:
 * - `+ n`, visible when group is collapsed, and expands all lines when clicked
 * - `Show less`, visible when group is expanded. Collapses group back when clicked.
 *
 * ChipGroup only allows Chips or ChipGroup.ClearAllButton as children. Chips UI props such as `emphasis` and `size`
 * must be passed to `ChipGroup` to ensure visual consistency of the grouped chips.
 *
 * ChipGroup.ClearAllButton is a direct child of ChipGroup. When provided, it shows a transparent button at the
 * last index of the group, either when collapsed or expanded, with a label stating "Clear all" by
 * default. It accepts a `onClick` prop to run a callback function when clicked.
 *
 * Only a single instance of `ChipGroup.ClearAllButton` is allowed on a ChipGroup.
 *
 * **Heads up for unit tests!**
 * Don't worry about checking for the collapse/expand functionality, as this is been covered by Crystal's unit tests.
 * But if do wish to go down that road, you'll need to set JSDom's client and scroll width to do so.
 * Check the ChipGroup components tests for examples.
 *
 */
export function ChipGroup({
  children: _children,
  emphasis: _emphasis = 'outlined',
  lines = 1,
  size = 'sm',
  variant,
  width,
}: ChipGroupProps) {
  const emphasis = variant ?? _emphasis;
  const theme = useFullTheme();
  const ref = useRef<HTMLDivElement>(null);

  const children = isChildType(_children, React.Fragment)
    ? (_children as React.ReactElement).props.children
    : _children;

  // Checking how many children are there and if they're all Chips
  const numChildrenTotal = React.Children.count(children);

  const chipChildren = findChildrenOfType(children, [Chip]);
  const clearAllChildren = findChildrenOfType(children, [ChipGroup.ClearAllButton]);

  const numChipsTotal = chipChildren.length + clearAllChildren.length;

  if (clearAllChildren.length > 1) {
    throw new Error('You can provide only one ChipGroup.ClearAllButton component.');
  }

  if (numChildrenTotal !== numChipsTotal) {
    throw new Error('Only Chip and ChipGroup.ClearAllButton can be used as children of ChipGroup.');
  }

  if ((typeof lines != 'number' && lines != 'all') || (typeof lines === 'number' && lines < 0)) {
    throw new Error(
      'ChipGroup only accepts positive number values or "all" as values for the line prop.',
    );
  }

  // checking for size/emphasis/width set at Chip level
  if (!chipChildren.every(child => !(child.props.size && child.props.size !== size))) {
    throw new Error('Chip as children of ChipGroup inherit `size` from its parent.');
  }

  if (!chipChildren.every(child => !(child.props.emphasis && child.props.emphasis !== emphasis))) {
    throw new Error('Chip as children of ChipGroup inherit `emphasis` from its parent.');
  }

  if (!chipChildren.every(child => !(child.props.variant && child.props.variant !== variant))) {
    throw new Error('Chip as children of ChipGroup inherit `variant` from its parent.');
  }

  if (!chipChildren.every(child => !(child.props.width && child.props.width !== width))) {
    throw new Error('Chip as children of ChipGroup inherit `width` from its parent.');
  }

  const clearAllChild: React.ReactElement | null = clearAllChildren.length
    ? clearAllChildren[0]
    : null;
  const numChipsOnly = chipChildren.length;

  const hasClearAllChild = !!clearAllChild;

  // memoizing these because they get used on the useLayoutEffect down below
  const gap = useMemo(() => {
    return {
      vertical: theme.spacing[4],
      horizontal: theme.spacing[2],
    };
  }, [theme]);

  const chipDefaultProps = { size, emphasis, width };

  /**
   * The ChipGroup height will depend on:
   * - How many lines are passed as a props, if they're a number
   * - The actual chip size chosen
   * Also takes in consideration the vertical spacing of 16px and the
   * "Clear all" box-shadow on hover when calculating the group height
   */
  const chipGroupHeight =
    lines === 'all'
      ? 'auto'
      : typeof lines === 'number' && lines > 1
        ? (heightMap[size] + parseInt(gap.vertical)) * lines
        : heightMap[size] + parseInt(theme.spacing[2]) * 2;

  const [numChipsToShow, setNumChipsToShow] = useState(numChipsOnly);
  const [showAll, setShowAll] = useState(false);
  const [minWidth, setMinWidth] = useState(0);

  useLayoutEffect(() => {
    // calculates at what index chips are overflowing
    // and whats the min width of the group
    const calculateDimensions = () => {
      if (ref?.current) {
        const { current } = ref;
        const allGroupChildren = current?.children;

        if (current.scrollHeight > current.clientHeight) {
          const firstOverflownChip = Array.from(allGroupChildren).findIndex(
            child => (child as HTMLElement).offsetTop - current?.offsetTop > current?.clientHeight,
          );

          const extraChipsToHide = hasClearAllChild ? 2 : 1;
          const minNumOfChips =
            firstOverflownChip - extraChipsToHide <= 0 ? 1 : firstOverflownChip - extraChipsToHide;

          setNumChipsToShow(minNumOfChips);

          // minWidth is now the sum of the 1st chip + the last visible + (horizontal gap * 2)
          // which holds all the "control" chips (+ n and clear all)
          if (allGroupChildren[0]) {
            setMinWidth(
              allGroupChildren[0].clientWidth +
                allGroupChildren[minNumOfChips].clientWidth +
                parseInt(gap.horizontal) * 2,
            );
          }
        } else {
          setNumChipsToShow(numChipsOnly);
        }
      }
    };

    let timeout: NodeJS.Timeout;

    if (lines === 'all') {
      setShowAll(true);
    } else {
      calculateDimensions();
      window.addEventListener('resize', function () {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          calculateDimensions();
        }, DEBOUNCE_TIMEOUT);
      });
    }

    return () => {
      window.removeEventListener('resize', calculateDimensions);
      if (timeout) clearTimeout(timeout);
    };
  }, [hasClearAllChild, gap, lines, numChipsOnly, ref]);

  return (
    <div
      css={{
        alignItems: 'flex-start',
        alignContent: 'flex-start',
        display: 'flex',
        flexWrap: 'wrap',
        gap: `${gap.vertical} ${gap.horizontal}`,
        height: showAll ? 'auto' : chipGroupHeight,
        overflowY: showAll ? 'visible' : 'hidden',

        // the following two declarations make up for a bit of extra space
        // for the "Clear all" box-shadow on hover
        padding: `${theme.spacing[2]} 0`,
        margin: `-${theme.spacing[2]} 0`,

        // ensuring at least the 1st chip + "+ n" control + clear all (when available)
        // are always visible
        minWidth: showAll ? 'auto' : `${minWidth}px`,
      }}
      ref={ref}
    >
      {React.Children.map(chipChildren, (child, index) => {
        const chipChild = (child as React.ReactElement).props;
        return (
          <>
            <div
              css={{
                height: heightMap[size],
                pointerEvents: showAll ? undefined : index < numChipsToShow ? 'auto' : 'none',
                visibility: showAll ? undefined : index < numChipsToShow ? 'visible' : 'hidden',
                whiteSpace: 'nowrap',
              }}
              key={index}
            >
              <Chip {...chipDefaultProps} onClick={chipChild.onClick} onDelete={chipChild.onDelete}>
                {chipChild.children}
              </Chip>
            </div>

            {/*
              if:
              - we're not showing all chips (!showAll)
              - there's over one chip to show (numChipsOnly > 1 )
              - the index of this element is the same as the last one before overflowing (index === numChipsToShow - 1)
              - there's at least one overflown chip (numChipsOnly - numChipsToShow > 0)
              then, insert the '+ n' group control chip
            */}
            {!showAll &&
              numChipsOnly > 1 &&
              numChipsOnly - numChipsToShow > 0 &&
              index === numChipsToShow - 1 && (
                <div
                  css={{
                    columnGap: gap.horizontal,
                    display: 'inline-flex',
                    height: heightMap[size],
                    whiteSpace: 'nowrap',
                  }}
                  key={`${index}-showall`}
                >
                  <Chip {...chipDefaultProps} onClick={() => setShowAll(true)}>
                    {`+ ${numChipsOnly - numChipsToShow}`}
                  </Chip>

                  {hasClearAllChild && (
                    <ChipGroup.ClearAllButton
                      onClick={(clearAllChild as React.ReactElement).props.onClick}
                    >
                      {(clearAllChild as React.ReactElement).props.children}
                    </ChipGroup.ClearAllButton>
                  )}
                </div>
              )}

            {/*
              and if in case this particular chip is the last one, we're showing all chips
              and there's less chips to show than the total available, show the
              'show less' group control chip
            */}
            {numChipsToShow < numChipsOnly && showAll && index === numChipsOnly - 1 && (
              <div
                css={{
                  columnGap: gap.horizontal,
                  display: 'inline-flex',
                  height: heightMap[size],
                  whiteSpace: 'nowrap',
                }}
                key={`${index}-showless`}
              >
                <Chip {...chipDefaultProps} onClick={() => setShowAll(false)}>
                  Show less
                </Chip>

                {hasClearAllChild && (
                  <ChipGroup.ClearAllButton
                    onClick={(clearAllChild as React.ReactElement).props.onClick}
                  >
                    {(clearAllChild as React.ReactElement).props.children}
                  </ChipGroup.ClearAllButton>
                )}
              </div>
            )}

            {/*
              we're not overflowing and this is the last index,
              so let's show the "clear all" btn
            */}
            {numChipsToShow >= numChipsOnly && index === numChipsOnly - 1 && hasClearAllChild && (
              <div
                css={{
                  columnGap: gap.horizontal,
                  display: 'inline-flex',
                  height: heightMap[size],
                  whiteSpace: 'nowrap',
                }}
                key={`${index}-delete`}
              >
                <ChipGroup.ClearAllButton
                  onClick={(clearAllChild as React.ReactElement).props.onClick}
                >
                  {(clearAllChild as React.ReactElement).props.children}
                </ChipGroup.ClearAllButton>
              </div>
            )}
          </>
        );
      })}
    </div>
  );
}

interface ChipGroupClearAllButtonProps {
  /**
   * A callback function that is called when "Clear all" is clicked.
   */
  onClick: () => void;

  /**
   * Label for the Delete button. Defaults to "Clear all".
   */
  children?: string;
}

ChipGroup.ClearAllButton = function ChipGroupClearAllButton({
  onClick,
  children = 'Clear all',
}: ChipGroupClearAllButtonProps) {
  const theme = useFullTheme();

  return (
    <button
      onClick={() => onClick()}
      type="button"
      css={{
        ...theme.borders.transparent,
        appearance: 'none',
        borderRadius: theme.borderRadiuses.control,
        padding: `0 ${theme.spacing[3]}`,
        backgroundColor: 'transparent',
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: theme.colors.bgPrimaryHighlightHover,
          boxShadow: theme.elevations.z1,
        },
        '&:active': {
          backgroundColor: theme.colors.bgPrimaryHighlightPressed,
          boxShadow: 'none',
        },
        '&:focus-visible': {
          borderRadius: theme.borderRadiuses.control,
          outlineOffset: '1px',
        },
      }}
    >
      <InternalText as="span" intent="primary" variant="labelS" contrast="mid">
        {children ? children : 'Clear all'}
      </InternalText>
    </button>
  );
};
